// hotel/F&B/HotelFBMgmt.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Select,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Avatar,
  Tooltip,
  Divider,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Fade,
  Zoom,
  Slide,
  Grow,
  Fab,
  Skeleton,
  StepConnector,
  stepConnectorClasses,
  styled as muiStyled,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Delete,
  Visibility,
  Search,
  Refresh,
  Restaurant,
  Receipt,
  TrendingUp,
  Schedule,
  CheckCircle,
  DeliveryDining,
  TakeoutDining,
  RoomService,
  AttachMoney,
  Fastfood,
  LocalBar,
  Remove,
  Category,
  Print,
  Cancel,
  ShoppingCart,
  LocalHotel,
  Inventory,
  Warning,
  DoneAll,
  HourglassEmpty,
  EmojiEmotions,
  Phone,
  Person,
  TableRestaurant,
  Kitchen,
  AddCircle,
  NavigateNext,
  NavigateBefore,
  Check,
  CleaningServices,
  EventSeat,
  WarningAmber,
  PlayArrow,
  History,
  LockClock,
  Today
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../../../../components/sidebars/sidebar_f&b';
import TopNav from '../../../../components/TopNav_hotelStaff';
import printingService from './printingService';

// Animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const OrderCard = styled(Card)(({ theme, status }) => {
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending': return theme.palette.warning.light;
    case 'confirmed': return theme.palette.info.light;
    case 'completed': return theme.palette.grey[300];
    case 'cancelled': return theme.palette.error.light;
    default: return theme.palette.grey[300];
  }
};

  return {
    border: `2px solid ${getStatusColor(status)}`,
    borderRadius: '16px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    overflow: 'hidden',
    animation: `${fadeInUp} 0.5s ease-out`,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
    },
  };
});

const StatusBadge = styled(Box)(({ theme, status }) => {
 const getColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending': return theme.palette.warning.main;
    case 'confirmed': return theme.palette.info.main;
    case 'completed': return theme.palette.grey[600];
    case 'cancelled': return theme.palette.error.main;
    default: return theme.palette.grey[500];
  }
};

  return {
    backgroundColor: getColor(status),
    color: 'white',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    animation: `${pulse} 1s infinite`,
  };
});

const InventoryItemCard = styled(Card)(({ theme, selected, lowstock }) => ({
  border: selected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: lowstock ? theme.palette.error.lighter : theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
  '&::before': lowstock ? {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    backgroundColor: theme.palette.error.main,
  } : {},
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '20px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[12],
  },
}));

const FloatingActionButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  '&:hover': {
    transform: 'scale(1.1)',
    transition: 'transform 0.3s ease',
  },
}));

const ColorlibConnector = muiStyled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #0f041f 0%, #330686 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #0f041f 0%, #330686 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = muiStyled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #0f041f 0%, #330686 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #0f041f 0%, #330686 100%)',
  }),
}));

// Constants
const ORDER_TYPES = [
  { value: 'dine_in', label: 'Bar/Skyline', icon: <LocalBar />, color: '#1976D2', description: 'Dine at our bar or skyline lounge' },
  { value: 'Lounge', label: 'Lounge/Arena', icon: <LocalBar />, color: '#1976D2', description: 'Relax in our premium lounge area' },
  { value: 'room_service', label: 'Room Service', icon: <RoomService />, color: '#2E7D32', description: 'Delivered to your hotel room' },
  { value: 'takeaway', label: 'Takeaway', icon: <TakeoutDining />, color: '#ED6C02', description: 'Pick up your order to go' },
  { value: 'delivery', label: 'Delivery', icon: <DeliveryDining />, color: '#0288D1', description: 'Delivered to your address' },
];


const ORDER_STATUSES = [
  { value: 'pending', label: 'Pending', icon: <HourglassEmpty />, color: '#FFC107' },
  { value: 'confirmed', label: 'Confirmed', icon: <CheckCircle />, color: '#1976D2' },
  { value: 'completed', label: 'Completed', icon: <CheckCircle />, color: '#2E7D32' },
];

// API URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME || 'Frankville Hotels';
const TAX_RATE = parseFloat(process.env.REACT_APP_HOTEL_TAX) || 0;
const SERVICE_CHARGE_RATE = parseFloat(process.env.REACT_APP_HOTEL_SERVICE_CHARGE) || 0;

// Helper Functions
const getCurrentStaffId = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.staff_id || storedProfile.id || storedProfile.staffId || null;
  } catch (error) {
    console.error('Error getting staff ID:', error);
    return null;
  }
};

const getCurrentStaffName = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.first_name || storedProfile.name || 'Staff';
  } catch (error) {
    console.error('Error getting staff name:', error);
    return 'Staff';
  }
};

const getHotelCode = () => {
  try {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
  } catch (error) {
    console.error('Error getting hotel code:', error);
    return '';
  }
};

// Helper to get date string in YYYY-MM-DD format
const getDateOnly = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  return dateTimeStr.split(' ')[0].split('T')[0];
};

// API Service - FIXED (no duplicates)
const apiService = {
  getInventoryCategories: async () => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/inventory_categories.php`, {
      params: { hotel_code: getHotelCode() }
    });
    return response.data;
  },

  getInventoryItems: async (category = '', includeOutOfStock = false, search = '') => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/inventory.php`, {
      params: {
        hotel_code: getHotelCode(),
        category,
        include_out_of_stock: includeOutOfStock,
        search
      }
    });
    return response.data;
  },

  getTables: async (section = '') => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/f_b_tables.php`, {
      params: {
        hotel_code: getHotelCode(),
        section: section
      }
    });
    return response.data;
  },

  getRooms: async () => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/rooms.php`, {
      params: { hotel_code: getHotelCode() }
    });
    return response.data;
  },

 getOrders: async (params = {}) => {
    const staffId = params.staff_id || getCurrentStaffId();
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/orders.php`, {
        params: { 
            hotel_code: getHotelCode(), 
            staff_id: staffId,  // Always pass the current staff ID
            ...params 
        }
    });
    return response.data;
},

  createOrder: async (orderData) => {
    const response = await axios.post(`${API_BASE_URL}/hotel/F&B/orders.php`, orderData);
    return response.data;
  },

  addToOrder: async (orderData) => {
    const response = await axios.post(`${API_BASE_URL}/hotel/F&B/orders.php`, {
      ...orderData,
      action: 'add_to_order'
    });
    return response.data;
  },

  removeOrderItem: async (orderItemId, orderId) => {
    const response = await axios.post(`${API_BASE_URL}/hotel/F&B/orders.php`, {
      action: 'remove_item',
      order_item_id: orderItemId,
      order_id: orderId,
      hotel_code: getHotelCode()
    });
    return response.data;
  },

  updateOrderStatus: async (orderId, status, cancellationReason = null) => {
    const requestData = {
      id: orderId,
      hotel_code: getHotelCode(),
      status,
      served_by: getCurrentStaffId(),
      cancelled_by: getCurrentStaffId()
    };
    if (cancellationReason) {
      requestData.cancellation_reason = cancellationReason;
    }
    const response = await axios.put(`${API_BASE_URL}/hotel/F&B/orders.php`, requestData);
    return response.data;
  },

  // Add to apiService object
completeOrderBackdated: async (orderId) => {
  const response = await axios.post(`${API_BASE_URL}/hotel/F&B/orders.php`, {
    action: 'complete_backdated',
    order_id: orderId,
    hotel_code: getHotelCode(),
    completed_by: getCurrentStaffId(),
    completed_by_name: getCurrentStaffName()
  });
  return response.data;
},

  getStatistics: async (period = 'today') => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/orders.php`, {
      params: {
        action: 'staff_statistics',
        hotel_code: getHotelCode(),
        staff_id: getCurrentStaffId(),
        period
      }
    });
    return response.data;
  },

  checkCanCreateOrder: async () => {
    const response = await axios.get(`${API_BASE_URL}/hotel/F&B/can_create_order.php`, {
      params: { 
        hotel_code: getHotelCode(),
        staff_id: getCurrentStaffId()
      }
    });
    return response.data;
  },

  getCurrentShift: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/F&B/shifts.php`, {
        params: {
          hotel_code: getHotelCode(),
          staff_id: getCurrentStaffId(),
          include_history: 'true'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting shift:', error);
      return { success: false, active_shift: null, is_day_closed: false };
    }
  },

  checkCanStartShift: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/F&B/can_start_shift.php`, {
        params: { hotel_code: getHotelCode() }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking shift start:', error);
      return { can_start: false, reasons: ['Unable to check conditions'], has_pending_orders: false, pending_orders: [] };
    }
  },

  getPendingOrders: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/hotel/F&B/orders.php`, {
        params: { 
          hotel_code: getHotelCode(),
         // In the orderData object, add:
status: 'confirmed',
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching pending orders:', error);
      return { success: false, orders: [] };

    }
  },

 startShift: async () => {
  // IMPORTANT: Do NOT call checkCanStartShift here - that should be checked BEFORE calling this function
  // This function should only create the shift, not validate conditions
  const response = await axios.post(`${API_BASE_URL}/hotel/F&B/shifts.php`, {
    hotel_code: getHotelCode(),
    staff_id: getCurrentStaffId(),
    staff_name: getCurrentStaffName()
  });
  return response.data;
}};

const HotelFBMgmt = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // State management
  const [orders, setOrders] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filteredInventoryItems, setFilteredInventoryItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  // Pagination
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);
const [pendingOrderToComplete, setPendingOrderToComplete] = useState(null);

  // Dialog states
  const [openOrderDialog, setOpenOrderDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openOrderDetailsDialog, setOpenOrderDetailsDialog] = useState(false);
  const [openAddToOrderDialog, setOpenAddToOrderDialog] = useState(false);
  const [openRemoveItemDialog, setOpenRemoveItemDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderForAdd, setSelectedOrderForAdd] = useState(null);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [removingItem, setRemovingItem] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Order wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedOrderType, setSelectedOrderType] = useState(null);

  
  // Day Closure & Shift State
  const [isDayClosed, setIsDayClosed] = useState(false);
  const [canCreateOrder, setCanCreateOrder] = useState(false);
  const [currentShift, setCurrentShift] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [shiftStarting, setShiftStarting] = useState(false);

  // Pending orders state
  const [pendingOrdersDialogOpen, setPendingOrdersDialogOpen] = useState(false);
  const [pendingOrdersCheck, setPendingOrdersCheck] = useState({
    has_pending: false,
    pending_count: 0,
    pending_orders: []
  });
  const [pendingOrderToCancel, setPendingOrderToCancel] = useState(null);
  const [pendingCancelReason, setPendingCancelReason] = useState('');

  // Order form state
  const [orderForm, setOrderForm] = useState({
    hotel_code: getHotelCode(),
    order_type: '',
    table_number: '',
    room_number: '',
    guest_name: '',
    guest_phone: '',
    special_instructions: '',
    items: [],
  });

  // Add to order state
  const [addToOrderItems, setAddToOrderItems] = useState([]);
  const [addToOrderCategory, setAddToOrderCategory] = useState('');
  const [addToOrderItemSearch, setAddToOrderItemSearch] = useState('');
  const [addToOrderIncludeOutOfStock, setAddToOrderIncludeOutOfStock] = useState(false);
  const [addToOrderFilteredItems, setAddToOrderFilteredItems] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [includeOutOfStock, setIncludeOutOfStock] = useState(false);
  const [itemSearch, setItemSearch] = useState('');

  
  // Wizard steps
  const wizardSteps = ['Select Order Outlet', 'Customer Details', 'Select Items', 'Review Order'];

  // Filter orders based on current shift and history toggle
  const getFilteredOrders = useCallback(() => {
    let filtered = [...orders];
    
    // Filter by current shift if not showing history
    if (!showHistory && currentShift) {
      filtered = filtered.filter(order => order.shift_id === currentShift.id);
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order =>
        order.order_number?.toLowerCase().includes(query) ||
        order.table_number?.toLowerCase().includes(query) ||
        (order.room_number?.toString().toLowerCase().includes(query)) ||
        order.guest_name?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [orders, showHistory, currentShift, searchQuery]);

  const filteredOrders = getFilteredOrders();

  const currentOrders = filteredOrders.slice(
    ordersPage * ordersRowsPerPage,
    ordersPage * ordersRowsPerPage + ordersRowsPerPage
  );

  // Load tables when dialog opens based on selected order type
  useEffect(() => {
    if (openOrderDialog && selectedOrderType) {
      if (selectedOrderType === 'dine_in') {
        loadTables('Bar');
      } else if (selectedOrderType === 'Lounge') {
        loadTables('Lounge');
      }
    }
  }, [openOrderDialog, selectedOrderType]);

  // Filter inventory items
  useEffect(() => {
    filterInventoryItems();
  }, [selectedCategory, includeOutOfStock, itemSearch, inventoryItems]);

  // Filter add-to-order inventory items
  useEffect(() => {
    let filtered = [...inventoryItems];
    if (addToOrderCategory) {
      filtered = filtered.filter(item => item.category === addToOrderCategory);
    }
    if (!addToOrderIncludeOutOfStock) {
      filtered = filtered.filter(item => item.current_stock > 0);
    }
    if (addToOrderItemSearch) {
      const searchLower = addToOrderItemSearch.toLowerCase();
      filtered = filtered.filter(item =>
        item.item_name.toLowerCase().includes(searchLower) ||
        item.category.toLowerCase().includes(searchLower)
      );
    }
    setAddToOrderFilteredItems(filtered);
  }, [addToOrderCategory, addToOrderIncludeOutOfStock, addToOrderItemSearch, inventoryItems]);

  // Filter inventory items function
  const filterInventoryItems = () => {
    let filtered = [...inventoryItems];
    if (selectedCategory) {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }
    if (!includeOutOfStock) {
      filtered = filtered.filter(item => item.current_stock > 0);
    }
    if (itemSearch) {
      const searchLower = itemSearch.toLowerCase();
      filtered = filtered.filter(item =>
        item.item_name.toLowerCase().includes(searchLower) ||
        item.category.toLowerCase().includes(searchLower)
      );
    }
    setFilteredInventoryItems(filtered);
  };

  // Load data functions
const loadData = async () => {
  setLoading(true);
  try {
    await Promise.all([
      loadOrders(),
      loadCategories(),
      loadInventoryItems(),
      loadRooms(),
      loadStatistics(),
      checkShiftAndClosureStatus()  // This only checks status, does NOT create shift
    ]);
  } catch (error) {
    console.error('Error loading data:', error);
    showSnackbar('Failed to load data: ' + error.message, 'error');
  } finally {
    setLoading(false);
  }
};
 const loadOrders = async () => {
    try {
        // Always pass the current staff ID to ensure waiter-specific filtering
        const staffId = getCurrentStaffId();
        const data = await apiService.getOrders({ staff_id: staffId });
        if (data.success) {
            setOrders(data.orders || []);
        }
    } catch (error) {
        console.error('Error loading orders:', error);
    }
};

  const loadCategories = async () => {
    try {
      const data = await apiService.getInventoryCategories();
      if (data.success) {
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadInventoryItems = async () => {
    setInventoryLoading(true);
    try {
      const data = await apiService.getInventoryItems();
      if (data.success) {
        setInventoryItems(data.inventory || []);
        setFilteredInventoryItems(data.inventory || []);
      }
    } catch (error) {
      console.error('Error loading inventory items:', error);
    } finally {
      setInventoryLoading(false);
    }
  };

  const loadTables = async (section = '') => {
    try {
      const data = await apiService.getTables(section);
      if (data.success) {
        setTables(data.tables || []);
        if (data.tables?.length === 0 && section) {
          showSnackbar(`No tables found in ${section} section. Please add tables first.`, 'warning');
        }
      }
    } catch (error) {
      console.error('Error loading tables:', error);
      showSnackbar('Failed to load tables: ' + error.message, 'error');
    }
  };

  const loadRooms = async () => {
    try {
      const data = await apiService.getRooms();
      if (data.success) {
        setRooms(data.rooms || []);
      }
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await apiService.getStatistics();
      if (data.success) {
        setStatistics(data.statistics);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  // Handle cancel pending order from previous days
  // Replace handleCancelPendingOrder with:
const handleCompletePendingOrder = async () => {
  if (!pendingOrderToComplete) return;
  
  setLoading(true);
  try {
    const result = await apiService.completeOrderBackdated(pendingOrderToComplete.id);
    if (result.success) {
      showSnackbar(`Order ${pendingOrderToComplete.order_number} completed successfully (backdated to ${new Date(result.completed_at).toLocaleDateString()})`, 'success');
      // Reload all data
      await loadData();
      await checkShiftAndClosureStatus();
      setPendingOrdersDialogOpen(false);
      setPendingOrderToComplete(null);
    } else {
      showSnackbar(result.error || 'Failed to complete order', 'error');
    }
  } catch (error) {
    console.error('Error completing order:', error);
    showSnackbar(error.response?.data?.error || error.message, 'error');
  } finally {
    setLoading(false);
  }
};

 
// Update the checkShiftAndClosureStatus function in HotelFBMgmt.js

const checkShiftAndClosureStatus = async () => {
  try {
    // First check if we can create orders (this checks for pending orders)
    const canCreateData = await apiService.checkCanCreateOrder();
    
    // Get shift status
    const shiftData = await apiService.getCurrentShift();
    
    setIsDayClosed(shiftData.is_day_closed || false);
    setCurrentShift(shiftData.active_shift);
    
    const isCashier = shiftData.is_cashier || false;
    
    // Update canStartShift logic based on user type
    if (shiftData.is_day_closed) {
      // Day is closed - only cashier can start new day
      if (isCashier) {
        setCanCreateOrder(false);
        // Show cashier the option to start new day
      } else {
        setCanCreateOrder(false);
        showSnackbar('⚠️ Day has been closed by cashier. Only cashiers can start a new day.', 'error');
      }
    } else if (shiftData.active_shift) {
      // Shift exists - all staff can take orders
      setCanCreateOrder(true);
    } else {
      // No active shift - only cashier can start one
      setCanCreateOrder(false);
      if (!isCashier) {
        showSnackbar('⚠️ No active shift. Please wait for the cashier to start the day.', 'error');
      }
    }
    
    // Check for pending orders from previous days
    const canStartData = await apiService.checkCanStartShift();
    setPendingOrdersCheck({
      has_pending: canStartData.has_pending_orders || false,
      pending_count: canStartData.pending_orders?.length || 0,
      pending_orders: canStartData.pending_orders || []
    });
    
  } catch (error) {
    console.error('Error checking shift status:', error);
  }
}; 

const handleStartShift = async () => {
  setShiftStarting(true);
  try {
    // First check if we can start shift (checks for pending orders AND previous day closure)
    const canStartData = await apiService.checkCanStartShift();
    
    if (!canStartData.can_start) {
      const reasonsList = canStartData.reasons?.join('\n') || 'Cannot start shift';
      
      // Check if the reason is because previous day wasn't closed
      if (canStartData.reasons?.some(r => r.includes("not properly closed"))) {
        showSnackbar('❌ Cannot start shift. The cashier must close the previous day\'s operations first.', 'error');
      } else if (canStartData.has_pending_orders && canStartData.pending_orders?.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const previousDayOrders = canStartData.pending_orders.filter(order => {
          const orderDate = getDateOnly(order.created_at);
          return orderDate < today && order.status === 'pending';
        });
        
        if (previousDayOrders.length > 0) {
          setPendingOrdersCheck({
            has_pending: true,
            pending_count: previousDayOrders.length,
            pending_orders: previousDayOrders
          });
          setPendingOrdersDialogOpen(true);
        } else {
          showSnackbar(`❌ Cannot start shift:\n${reasonsList}`, 'error');
        }
      } else {
        showSnackbar(`❌ Cannot start shift:\n${reasonsList}`, 'error');
      }
      
      setShiftStarting(false);
      return;
    }
    
    // Only proceed if canStart is true (previous day is closed)
    const result = await apiService.startShift();
    if (result.success) {
      showSnackbar('✅ Shift started successfully! You can now take orders.', 'success');
      await checkShiftAndClosureStatus();
      await loadOrders();
      await loadStatistics();
    } else {
      showSnackbar(result.error || 'Failed to start shift', 'error');
    }
  } catch (error) {
    console.error('Error starting shift:', error);
    showSnackbar(error.message, 'error');
  } finally {
    setShiftStarting(false);
  }
};  // Show snackbar function
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Refresh inventory after cancellation
  const refreshInventoryAfterCancellation = async () => {
    await loadInventoryItems();
  };

  
  // Main data load effect - NO automatic shift creation
useEffect(() => {
  loadData();
  const interval = setInterval(() => {
    loadOrders();
    loadStatistics();
    checkShiftAndClosureStatus();
  }, 30000);
  return () => clearInterval(interval);
}, []);

  // Reset page when filters change
  useEffect(() => {
    setOrdersPage(0);
  }, [showHistory, searchQuery]);

  // Wizard Handlers
  const handleOpenOrderDialog = () => {
    if (isDayClosed) {
      showSnackbar('❌ Day is closed. Cannot create new orders. Please contact cashier.', 'error');
      return;
    }
    
    if (!currentShift) {
      showSnackbar('❌ No active shift. Please start your shift first.', 'error');
      return;
    }
    
    setActiveStep(0);
    setSelectedOrderType(null);
    setSelectedCategory('');
    setSelectedItems([]);
    setItemSearch('');
    setIncludeOutOfStock(false);
    setOrderForm({
      hotel_code: getHotelCode(),
      order_type: '',
      table_number: '',
      room_number: '',
      guest_name: '',
      guest_phone: '',
      special_instructions: '',
      items: [],
    });
    setOpenOrderDialog(true);
  };

  const handleNextStep = () => {
    if (activeStep === 0 && !selectedOrderType) {
      showSnackbar('Please select an order outlet', 'error');
      return;
    }
    if (activeStep === 1) {
      if (selectedOrderType === 'room_service' && !orderForm.room_number) {
        showSnackbar('Please select a room number', 'error');
        return;
      }
      if ((selectedOrderType === 'dine_in' || selectedOrderType === 'Lounge') && !orderForm.table_number) {
        showSnackbar('Please select a table number', 'error');
        return;
      }
      if ((selectedOrderType === 'takeaway' || selectedOrderType === 'delivery') && !orderForm.guest_name) {
        showSnackbar('Please enter guest name', 'error');
        return;
      }
      if ((selectedOrderType === 'takeaway' || selectedOrderType === 'delivery') && !orderForm.guest_phone) {
        showSnackbar('Please enter phone number', 'error');
        return;
      }
    }
    if (activeStep === 2 && selectedItems.length === 0) {
      showSnackbar('Please select at least one item', 'error');
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBackStep = () => {
    setActiveStep((prev) => prev - 1);
  };

  

  const handleSelectOrderType = (type) => {
    setSelectedOrderType(type.value);
    setOrderForm(prev => ({
      ...prev,
      order_type: type.value,
      table_number: '',
      room_number: ''
    }));
  };

  const handleCloseOrderDialog = () => {
    setOpenOrderDialog(false);
    setActiveStep(0);
    setSelectedOrderType(null);
  };

  const handleOpenAddToOrderDialog = (order) => {
    if (isDayClosed) {
      showSnackbar('❌ Day is closed. Cannot add items to orders.', 'error');
      return;
    }
    
    setSelectedOrderForAdd(order);
    setAddToOrderItems([]);
    setAddToOrderCategory('');
    setAddToOrderItemSearch('');
    setAddToOrderIncludeOutOfStock(false);
    setOpenAddToOrderDialog(true);
  };

  const handleCloseAddToOrderDialog = () => {
    setOpenAddToOrderDialog(false);
    setSelectedOrderForAdd(null);
    setAddToOrderItems([]);
  };

  const handleOrderInputChange = (field) => (event) => {
    const value = event.target.value;
    setOrderForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setSelectedItems([]);
  };

  const handleAddToOrderCategoryChange = (event) => {
    setAddToOrderCategory(event.target.value);
    setAddToOrderItems([]);
  };

  const handleItemSelection = (item) => {
    setSelectedItems(prev => {
      const existingItem = prev.find(i => i.id === item.id);
      if (existingItem) {
        return prev.map(i =>
          i.id === item.id
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      } else {
        return [...prev, { ...item, quantity: 1 }];
      }
    });
  };

  const handleAddToOrderItemSelection = (item) => {
    setAddToOrderItems(prev => {
      const existingItem = prev.find(i => i.id === item.id);
      if (existingItem) {
        return prev.map(i =>
          i.id === item.id
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      } else {
        return [...prev, { ...item, quantity: 1 }];
      }
    });
  };

  const handleItemQuantityChange = (itemId, quantity) => {
    if (quantity <= 0) {
      setSelectedItems(prev => prev.filter(item => item.id !== itemId));
    } else {
      setSelectedItems(prev =>
        prev.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        )
      );
    }
  };

  const handleAddToOrderQuantityChange = (itemId, quantity) => {
    if (quantity <= 0) {
      setAddToOrderItems(prev => prev.filter(item => item.id !== itemId));
    } else {
      setAddToOrderItems(prev =>
        prev.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        )
      );
    }
  };

  const handleRemoveOrderFormItem = (itemId) => {
    setSelectedItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleRemoveAddToOrderItem = (itemId) => {
    setAddToOrderItems(prev => prev.filter(item => item.id !== itemId));
  };

  const getItemPrice = (item) => {
    switch (selectedOrderType) {
      case 'dine_in': return item.price_dine_in || item.price;
      case 'Lounge': return item.price_vip_lounge || item.price_dine_in || item.price;
      case 'room_service': return item.price_room_service || item.price;
      case 'takeaway': return item.price_take_away || item.price;
      case 'delivery': return item.price_delivery || item.price;
      default: return item.price;
    }
  };

  const getItemPriceForOrder = (item, orderType) => {
    switch (orderType) {
      case 'dine_in': return item.price_dine_in || item.price;
      case 'Lounge': return item.price_vip_lounge || item.price_dine_in || item.price;
      case 'room_service': return item.price_room_service || item.price;
      case 'takeaway': return item.price_take_away || item.price;
      case 'delivery': return item.price_delivery || item.price;
      default: return item.price;
    }
  };

  const calculateOrderTotal = () => {
    const subtotal = selectedItems.reduce((total, item) => {
      const price = getItemPrice(item);
      return total + (parseFloat(price || 0) * item.quantity);
    }, 0);

    const taxAmount = (subtotal * TAX_RATE) / 100;
    const serviceChargeAmount = selectedOrderType === 'room_service' ? (subtotal * SERVICE_CHARGE_RATE) / 100 : 0;

    return {
      subtotal,
      tax: taxAmount,
      service_charge: serviceChargeAmount,
      total: subtotal + taxAmount + serviceChargeAmount
    };
  };

  const calculateAddToOrderTotal = () => {
    const subtotal = addToOrderItems.reduce((total, item) => {
      const price = getItemPriceForOrder(item, selectedOrderForAdd?.order_type || 'dine_in');
      return total + (parseFloat(price || 0) * item.quantity);
    }, 0);

    const taxAmount = (subtotal * TAX_RATE) / 100;
    const serviceChargeAmount = selectedOrderForAdd?.order_type === 'room_service' ? (subtotal * SERVICE_CHARGE_RATE) / 100 : 0;

    return {
      subtotal,
      tax: taxAmount,
      service_charge: serviceChargeAmount,
      total: subtotal + taxAmount + serviceChargeAmount
    };
  };

  const handleCreateOrder = async () => {
    if (selectedItems.length === 0) {
      showSnackbar('Please select at least one item', 'error');
      return;
    }

    const orderTotals = calculateOrderTotal();

    const itemsWithPricing = selectedItems.map(item => ({
      inventory_item_id: parseInt(item.id),
      quantity: item.quantity,
      unit_price: parseFloat(getItemPrice(item)),
      special_instructions: item.special_instructions || ''
    }));

    let formattedPhone = '';
    if (orderForm.guest_phone) {
      formattedPhone = orderForm.guest_phone.replace(/\D/g, '').substring(0, 15);
    }

   const orderData = {
  hotel_code: orderForm.hotel_code,
  order_type: selectedOrderType,
  table_number: orderForm.table_number || null,
  room_number: orderForm.room_number || null,
  guest_name: orderForm.guest_name || 'Walk-in Customer',
  guest_phone: formattedPhone,
  special_instructions: orderForm.special_instructions || '',
  items: itemsWithPricing,
  order_category: selectedCategory,
  total_amount: orderTotals.subtotal,
  tax_amount: orderTotals.tax,
  service_charge: orderTotals.service_charge,
  final_amount: orderTotals.total,
  created_by: getCurrentStaffId(),
  created_by_name: getCurrentStaffName(),
  status: 'confirmed'  // Add this line
};

    try {
      const result = await apiService.createOrder(orderData);
      if (result.success) {
        showSnackbar(`Order ${result.order_number} created successfully!`, 'success');

        if (result.ticket_data) {
          const ticketType = result.ticket_type || (selectedCategory.toLowerCase().includes('kitchen') ? 'KOT' : 'BOT');
          await printingService.printTicket(result.ticket_data, ticketType);
          showSnackbar(`📋 ${ticketType} ticket sent to printer!`, 'info');
        }

        await loadOrders();
        await loadInventoryItems();
        await loadStatistics();
        handleCloseOrderDialog();
      } else {
        showSnackbar(result.error || 'Failed to create order', 'error');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      showSnackbar(error.response?.data?.error || error.message, 'error');
    }
  };

  const handleAddToOrder = async () => {
    if (addToOrderItems.length === 0) {
      showSnackbar('Please select at least one item', 'error');
      return;
    }

    const itemsWithPricing = addToOrderItems.map(item => ({
      inventory_item_id: parseInt(item.id),
      quantity: item.quantity,
      unit_price: parseFloat(getItemPriceForOrder(item, selectedOrderForAdd?.order_type || 'dine_in')),
      special_instructions: item.special_instructions || ''
    }));

    const additionalTotals = calculateAddToOrderTotal();

    const orderData = {
      order_id: selectedOrderForAdd.id,
      hotel_code: getHotelCode(),
      items: itemsWithPricing,
      tax_amount: additionalTotals.tax,
      service_charge: additionalTotals.service_charge
    };

    try {
      const result = await apiService.addToOrder(orderData);
      if (result.success) {
        showSnackbar(`Items added to order ${selectedOrderForAdd.order_number} successfully!`, 'success');

        if (result.ticket_data) {
          const ticketType = result.ticket_type || 'BOT';
          await printingService.printTicket(result.ticket_data, ticketType);
          showSnackbar(`📋 Additional ${ticketType} ticket sent to printer!`, 'info');
        }

        await loadOrders();
        await loadInventoryItems();
        await loadStatistics();
        handleCloseAddToOrderDialog();
      } else {
        showSnackbar(result.error || 'Failed to add items', 'error');
      }
    } catch (error) {
      console.error('Error adding to order:', error);
      showSnackbar(error.response?.data?.error || error.message, 'error');
    }
  };

 const handleUpdateOrderStatus = async (orderId, newStatus, cancellationReason = null) => {
  try {
    const result = await apiService.updateOrderStatus(orderId, newStatus, cancellationReason);
    if (result.success) {
      showSnackbar(`Order ${newStatus.replace('_', ' ')} successfully!`, 'success');
      await loadOrders();
      await loadStatistics();
    } else {
      showSnackbar(result.error || 'Failed to update order status', 'error');
    }
  } catch (error) {
    console.error('Error updating order status:', error);
    showSnackbar('Failed to update order status: ' + error.message, 'error');
  }
};

  const handleOpenCancelDialog = (order) => {
    setOrderToCancel(order);
    setCancellationReason('');
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
    setOrderToCancel(null);
    setCancellationReason('');
  };

  const handleCancelOrder = async () => {
    if (!cancellationReason.trim()) {
      showSnackbar('Please provide a cancellation reason', 'error');
      return;
    }
    if (!orderToCancel) {
      showSnackbar('No order selected for cancellation', 'error');
      return;
    }
    await handleUpdateOrderStatus(orderToCancel.id, 'cancelled', cancellationReason);
  };

const handleOpenRemoveItemDialog = (order, item) => {
  setSelectedOrder(order);
  setItemToRemove(item);
  setOpenRemoveItemDialog(true);
};

  const handleCloseRemoveItemDialog = () => {
    setOpenRemoveItemDialog(false);
    setItemToRemove(null);
    setSelectedOrder(null);
  };

  const handleRemoveItem = async () => {
    if (!itemToRemove || !selectedOrder) {
      showSnackbar('Invalid item selection', 'error');
      return;
    }

    setRemovingItem(true);

    try {
      const result = await apiService.removeOrderItem(itemToRemove.id, selectedOrder.id);

      if (result.success) {
        showSnackbar(result.message, 'success');
        await loadOrders();
        await loadInventoryItems();
        await loadStatistics();

        if (openOrderDetailsDialog && selectedOrder?.id === selectedOrder.id) {
          const updatedOrder = orders.find(o => o.id === selectedOrder.id);
          if (updatedOrder) {
            setSelectedOrder(updatedOrder);
          } else {
            setOpenOrderDetailsDialog(false);
            setSelectedOrder(null);
          }
        }

        handleCloseRemoveItemDialog();
      } else {
        showSnackbar(result.error || 'Failed to remove item', 'error');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      showSnackbar(error.response?.data?.error || error.message, 'error');
    } finally {
      setRemovingItem(false);
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

 // Replace the existing printOrder function in HotelFBMgmt.js with this enhanced version
const printOrder = (order) => {
    try {
        const printWindow = window.open('', '_blank', 'width=400,height=600');
        if (!printWindow) {
            alert('Please allow popups for this site to print receipts.');
            return;
        }
        
        const orderType = ORDER_TYPES.find(ot => ot.value === order.order_type);
        const roomDisplay = order.room_number ? `Room ${order.room_number}${order.room_name ? ` - ${order.room_name}` : ''}` : '';
        const hotelName = HOTEL_NAME || 'Frankville Hotels';
        const staffName = getCurrentStaffName() || 'Staff';
        const currentDate = new Date().toLocaleString();
        const orderDate = new Date(order.created_at).toLocaleString();

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Order Receipt - ${order.order_number}</title>
                <style>
                    @media print {
                        @page { 
                            margin: 0; 
                            size: 80mm auto; 
                        }
                        body { 
                            font-family: 'Courier New', monospace;
                            font-size: 13px;
                            font-weight: bold;
                            margin: 0;
                            padding: 8px;
                            width: 80mm;
                        }
                        * {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                    }
                    * { 
                        margin: 0; 
                        padding: 0; 
                        box-sizing: border-box; 
                    }
                    body {
                        font-family: 'Courier New', monospace;
                        font-size: 13px;
                        font-weight: bold;
                        padding: 8px;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .header { 
                        text-align: center; 
                        margin-bottom: 12px; 
                    }
                    .header strong {
                        font-weight: 900;
                        font-size: 16px;
                    }
                    .title { 
                        font-size: 18px; 
                        font-weight: 900; 
                        margin: 5px 0; 
                    }
                    .divider { 
                        border-top: 2px dashed #000; 
                        margin: 8px 0; 
                    }
                    .divider-thick { 
                        border-top: 3px solid #000; 
                        margin: 8px 0; 
                    }
                    .item-row { 
                        display: flex; 
                        justify-content: space-between; 
                        margin: 4px 0;
                        font-weight: bold;
                        font-size: 13px;
                    }
                    .footer { 
                        text-align: center; 
                        margin-top: 12px; 
                        font-size: 12px;
                        font-weight: bold;
                    }
                    .order-detail {
                        font-weight: bold;
                        font-size: 13px;
                        margin: 3px 0;
                    }
                    .order-detail strong {
                        font-weight: 900;
                    }
                    .item-header {
                        display: flex;
                        justify-content: space-between;
                        font-weight: 900;
                        font-size: 14px;
                        border-bottom: 2px solid #000;
                        padding-bottom: 5px;
                        margin-bottom: 5px;
                    }
                    .total-row {
                        display: flex;
                        justify-content: space-between;
                        font-weight: 900;
                        font-size: 15px;
                        margin: 4px 0;
                    }
                    .grand-total {
                        display: flex;
                        justify-content: space-between;
                        font-weight: 900;
                        font-size: 18px;
                        margin: 8px 0;
                        border-top: 3px solid #000;
                        padding-top: 8px;
                    }
                    .thank-you {
                        text-align: center;
                        font-size: 16px;
                        font-weight: 900;
                        margin: 12px 0;
                    }
                    .timestamp {
                        font-weight: bold;
                        font-size: 12px;
                    }
                    .status-badge {
                        text-align: center;
                        font-weight: 900;
                        font-size: 14px;
                        padding: 5px;
                        background: #f0f0f0;
                        margin: 5px 0;
                    }
                    .order-type-label {
                        font-weight: 900;
                        color: #1976D2;
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div><strong style="font-size:18px;">${hotelName}</strong></div>
                    <div style="font-weight:bold; font-size:14px;">Food & Beverage Service</div>
                    <div class="divider"></div>
                </div>
                
                <div>
                    <div class="order-detail"><strong>Order #</strong> ${order.order_number}</div>
                    <div class="order-detail"><strong>Type:</strong> ${orderType?.label || order.order_type}</div>
                    ${order.table_number ? `<div class="order-detail"><strong>Table:</strong> ${order.table_number}</div>` : ''}
                    ${roomDisplay ? `<div class="order-detail"><strong>${roomDisplay}</strong></div>` : ''}
                    ${order.guest_name && order.guest_name !== 'Walk-in Customer' ? `<div class="order-detail"><strong>Guest:</strong> ${order.guest_name}</div>` : ''}
                    <div class="order-detail"><strong>Date:</strong> ${orderDate}</div>
                    <div class="status-badge"><strong>Status:</strong> ${order.status.toUpperCase()}</div>
                </div>
                
                <div class="divider"></div>
                
                <div>
                    <div class="item-header">
                        <span>QTY</span>
                        <span>ITEM</span>
                        <span>TOTAL</span>
                    </div>
                    ${order.items?.map(item => `
                        <div class="item-row">
                            <span style="font-weight:900;">${item.quantity}x</span>
                            <span style="font-weight:bold;">${item.item_name.substring(0, 25)}</span>
                            <span style="font-weight:900;">₦${(item.quantity * item.unit_price).toFixed(2)}</span>
                        </div>
                    `).join('')}
                </div>
                
                <div class="divider-thick"></div>
                
                <div>
                    <div class="total-row">
                        <span>Subtotal:</span>
                        <span style="font-weight:900;">₦${parseFloat(order.total_amount || 0).toFixed(2)}</span>
                    </div>
                    ${TAX_RATE > 0 && parseFloat(order.tax_amount || 0) > 0 ? `
                        <div class="total-row">
                            <span>Tax (${TAX_RATE}%):</span>
                            <span style="font-weight:900;">₦${parseFloat(order.tax_amount || 0).toFixed(2)}</span>
                        </div>
                    ` : ''}
                    ${order.order_type === 'room_service' && parseFloat(order.service_charge || 0) > 0 ? `
                        <div class="total-row">
                            <span>Service Charge (${SERVICE_CHARGE_RATE}%):</span>
                            <span style="font-weight:900;">₦${parseFloat(order.service_charge || 0).toFixed(2)}</span>
                        </div>
                    ` : ''}
                </div>
                
                <div class="grand-total">
                    <span>TOTAL:</span>
                    <span>₦${parseFloat(order.final_amount || 0).toFixed(2)}</span>
                </div>
                
                ${order.special_instructions ? `
                    <div class="divider"></div>
                    <div style="font-weight:bold; margin:5px 0;">
                        <strong>📝 Special Instructions:</strong><br>
                        ${order.special_instructions}
                    </div>
                ` : ''}
                
                <div class="divider"></div>
                
                <div class="thank-you">Thank You! Please come again.</div>
                
                <div class="footer">
                    <span class="timestamp">${currentDate}</span><br>
                    Served by: ${staffName}<br>
                    ${hotelName} F&B System
                </div>
                
                <script>
                    window.onload = function() {
                        window.print();
                        setTimeout(function() { 
                            window.close(); 
                        }, 800);
                    }
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();
    } catch (error) {
        console.error('Error printing order:', error);
        alert('Failed to print receipt. Please try again.');
    }
};
  // Wizard Step Components
  const renderStep0 = () => (
    <Grid container spacing={3}>
      {ORDER_TYPES.map((type) => (
        <Grid item xs={12} sm={6} md={4} key={type.value}>
          <Card
            sx={{
              cursor: 'pointer',
              border: selectedOrderType === type.value ? '2px solid #1976D2' : '1px solid #e0e0e0',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 3
              }
            }}
            onClick={() => handleSelectOrderType(type)}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: type.color, width: 60, height: 60, margin: '0 auto 16px' }}>
                {type.icon}
              </Avatar>
              <Typography variant="h6" gutterBottom>
                {type.label}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {type.description}
              </Typography>
              {selectedOrderType === type.value && (
                <Chip
                  label="Selected"
                  color="primary"
                  size="small"
                  icon={<Check />}
                  sx={{ mt: 2 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderStep1 = () => (
    <Grid container spacing={3}>
      {(selectedOrderType === 'dine_in' || selectedOrderType === 'Lounge') && (
        <Grid item xs={12}>
          <FormControl fullWidth required>
            <InputLabel>Select Table Number *</InputLabel>
            <Select
              value={orderForm.table_number}
              onChange={handleOrderInputChange('table_number')}
              label="Select Table Number *"
              renderValue={(selected) => {
                const table = tables.find(t => t.table_number === selected);
                if (!table) return selected;
                return `Table ${table.table_number} - ${table.status.toUpperCase()}`;
              }}
            >
              {tables.length === 0 ? (
                <MenuItem disabled>
                  <em>No tables available in this section</em>
                </MenuItem>
              ) : (
                tables.map(table => {
                  const isAvailable = table.status === 'available';
                  const isOccupied = table.status === 'occupied';
                  const isMaintenance = table.status === 'maintenance';
                  const isCleaning = table.status === 'cleaning';
                  const isReserved = table.status === 'reserved';
                  const isDisabled = !isAvailable;
                  
                  let statusColor = '';
                  let statusIcon = null;
                  if (isOccupied) {
                    statusColor = '#d32f2f';
                    statusIcon = <Cancel sx={{ fontSize: 16, color: '#d32f2f' }} />;
                  } else if (isMaintenance) {
                    statusColor = '#ff9800';
                    statusIcon = <Warning sx={{ fontSize: 16, color: '#ff9800' }} />;
                  } else if (isCleaning) {
                    statusColor = '#2196f3';
                    statusIcon = <CleaningServices sx={{ fontSize: 16, color: '#2196f3' }} />;
                  } else if (isReserved) {
                    statusColor = '#9c27b0';
                    statusIcon = <EventSeat sx={{ fontSize: 16, color: '#9c27b0' }} />;
                  } else {
                    statusColor = '#4caf50';
                    statusIcon = <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />;
                  }
                  
                  return (
                    <MenuItem 
                      key={table.id} 
                      value={table.table_number}
                      disabled={isDisabled}
                      sx={{
                        opacity: isDisabled ? 0.6 : 1,
                        backgroundColor: isDisabled ? '#f5f5f5' : 'transparent'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TableRestaurant />
                          <Typography>Table {table.table_number}</Typography>
                          <Chip 
                            size="small" 
                            label={`${table.capacity} seats`} 
                            variant="outlined" 
                            sx={{ ml: 1 }}
                          />
                        </Box>
                        <Chip
                          size="small"
                          icon={statusIcon}
                          label={table.status.toUpperCase()}
                          sx={{
                            backgroundColor: `${statusColor}20`,
                            color: statusColor,
                            fontWeight: 'bold',
                            '& .MuiChip-icon': {
                              color: statusColor
                            }
                          }}
                        />
                      </Box>
                    </MenuItem>
                  );
                })
              )}
            </Select>
            {tables.length > 0 && (
              <FormHelperText>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                  <Chip size="small" label={`Available: ${tables.filter(t => t.status === 'available').length}`} sx={{ bgcolor: '#4caf50', color: 'white' }} />
                  <Chip size="small" label={`Occupied: ${tables.filter(t => t.status === 'occupied').length}`} sx={{ bgcolor: '#d32f2f', color: 'white' }} />
                  <Chip size="small" label={`Reserved: ${tables.filter(t => t.status === 'reserved').length}`} sx={{ bgcolor: '#9c27b0', color: 'white' }} />
                  <Chip size="small" label={`Maintenance: ${tables.filter(t => t.status === 'maintenance').length}`} sx={{ bgcolor: '#ff9800', color: 'white' }} />
                  <Chip size="small" label={`Cleaning: ${tables.filter(t => t.status === 'cleaning').length}`} sx={{ bgcolor: '#2196f3', color: 'white' }} />
                </Box>
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
      )}

      {selectedOrderType === 'room_service' && (
        <Grid item xs={12}>
          <FormControl fullWidth required sx={{ minWidth: 250 }}>
            <InputLabel>Select Room Number *</InputLabel>
            <Select
              value={orderForm.room_number}
              onChange={handleOrderInputChange('room_number')}
              label="Select Room Number *"
            >
              {rooms.map(room => (
                <MenuItem key={room.id} value={room.number}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalHotel />
                    Room {room.number} - {room.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {(selectedOrderType === 'takeaway' || selectedOrderType === 'delivery') && (
        <>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Guest Name *"
              value={orderForm.guest_name}
              onChange={handleOrderInputChange('guest_name')}
              placeholder="Enter guest name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number *"
              value={orderForm.guest_phone}
              onChange={handleOrderInputChange('guest_phone')}
              placeholder="Enter phone number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              }}
              helperText="Phone number will be stored as text to prevent overflow"
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Special Instructions (Optional)"
          multiline
          rows={3}
          value={orderForm.special_instructions}
          onChange={handleOrderInputChange('special_instructions')}
          placeholder="Any special requests or instructions for preparation..."
        />
      </Grid>
    </Grid>
  );

  const renderStep2 = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <FormControl fullWidth required>
                <InputLabel>Select Category *</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  label="Select Category *"
                >
                  <MenuItem value="" disabled>
                    <em>Choose a category</em>
                  </MenuItem>
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
                {!selectedCategory && selectedItems.length === 0 && (
                  <FormHelperText error>Please select a category to view items</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search items..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant={includeOutOfStock ? "contained" : "outlined"}
                onClick={() => setIncludeOutOfStock(!includeOutOfStock)}
                startIcon={<Inventory />}
              >
                {includeOutOfStock ? "Hide Out of Stock" : "Show Out of Stock"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Fastfood /> Select Items
          <Chip label={`${filteredInventoryItems.length} items available`} size="small" color="primary" />
        </Typography>

        {inventoryLoading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto', borderRadius: 2 }}>
            {!selectedCategory ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Category sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  Please select a category first
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Choose a category from the dropdown above to view available items
                </Typography>
              </Box>
            ) : filteredInventoryItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Fastfood sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="textSecondary">
                  No items found in this category
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Try selecting a different category or add items to inventory
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredInventoryItems.map((item) => {
                  const price = getItemPrice(item);
                  const selected = selectedItems.some(i => i.id === item.id);
                  const isLowStock = item.current_stock <= item.min_stock;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <InventoryItemCard selected={selected} lowstock={isLowStock} onClick={() => handleItemSelection(item)}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="subtitle1" fontWeight="bold" noWrap>
                              {item.item_name}
                            </Typography>
                            {selected && (
                              <Chip label="Added" color="primary" size="small" icon={<CheckCircle />} />
                            )}
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            Stock: {item.current_stock} {item.unit}
                            {isLowStock && (
                              <Chip label="Low Stock" size="small" color="error" icon={<Warning />} sx={{ ml: 1 }} />
                            )}
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#FF6B35' }}>
                            ₦{parseFloat(price || 0).toFixed(2)}
                          </Typography>
                          {selected && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                              <IconButton size="small" onClick={(e) => {
                                e.stopPropagation();
                                const currentItem = selectedItems.find(i => i.id === item.id);
                                handleItemQuantityChange(item.id, (currentItem?.quantity || 1) - 1);
                              }} sx={{ bgcolor: '#f0f0f0' }}>
                                <Remove />
                              </IconButton>
                              <Typography variant="body1" fontWeight="bold">
                                {selectedItems.find(i => i.id === item.id)?.quantity || 1}
                              </Typography>
                              <IconButton size="small" onClick={(e) => {
                                e.stopPropagation();
                                const currentItem = selectedItems.find(i => i.id === item.id);
                                if (currentItem && currentItem.quantity < item.current_stock) {
                                  handleItemQuantityChange(item.id, (currentItem?.quantity || 0) + 1);
                                } else {
                                  showSnackbar(`Only ${item.current_stock} ${item.unit} available`, 'warning');
                                }
                              }} sx={{ bgcolor: '#f0f0f0' }}>
                                <Add />
                              </IconButton>
                            </Box>
                          )}
                        </CardContent>
                      </InventoryItemCard>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Paper>
        )}
      </Grid>

      {selectedItems.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart /> Order Summary ({selectedItems.length} items)
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f9f9f9' }}>
            <List>
              {selectedItems.map((item) => {
                const price = getItemPrice(item);
                return (
                  <ListItem key={item.id} divider>
                    <ListItemText
                      primary={item.item_name}
                      secondary={`Quantity: ${item.quantity} × ₦${parseFloat(price || 0).toFixed(2)}`}
                    />
                    <Typography variant="body1" fontWeight="bold" sx={{ mr: 2 }}>
                      ₦{(item.quantity * parseFloat(price || 0)).toFixed(2)}
                    </Typography>
                    <IconButton size="small" onClick={() => handleRemoveOrderFormItem(item.id)}>
                      <Delete />
                    </IconButton>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </Grid>
      )}
    </Grid>
  );

  const renderStep3 = () => {
    const totals = calculateOrderTotal();
    const selectedType = ORDER_TYPES.find(t => t.value === selectedOrderType);

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt /> Order Details
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="textSecondary">Order Outlet:</Typography>
                <Typography variant="body2" fontWeight="bold">{selectedType?.label}</Typography>
              </Box>
              {orderForm.table_number && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">Table Number:</Typography>
                  <Typography variant="body2" fontWeight="bold">{orderForm.table_number}</Typography>
                </Box>
              )}
              {orderForm.room_number && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">Room Number:</Typography>
                  <Typography variant="body2" fontWeight="bold">{orderForm.room_number}</Typography>
                </Box>
              )}
              {orderForm.guest_name && orderForm.guest_name !== 'Walk-in Customer' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">Guest Name:</Typography>
                  <Typography variant="body2" fontWeight="bold">{orderForm.guest_name}</Typography>
                </Box>
              )}
              {orderForm.guest_phone && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="textSecondary">Phone:</Typography>
                  <Typography variant="body2" fontWeight="bold">{orderForm.guest_phone}</Typography>
                </Box>
              )}
              {orderForm.special_instructions && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">Special Instructions:</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    {orderForm.special_instructions}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ bgcolor: '#f9f9f9' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney /> Payment Summary
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal:</Typography>
                <Typography>₦{totals.subtotal.toFixed(2)}</Typography>
              </Box>
              {TAX_RATE > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Tax ({TAX_RATE}%):</Typography>
                  <Typography>₦{totals.tax.toFixed(2)}</Typography>
                </Box>
              )}
              {selectedOrderType === 'room_service' && SERVICE_CHARGE_RATE > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Service Charge ({SERVICE_CHARGE_RATE}%):</Typography>
                  <Typography>₦{totals.service_charge.toFixed(2)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total Amount:</Typography>
                <Typography variant="h5" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  ₦{totals.total.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Fastfood /> Order Items ({selectedItems.length})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>Item</TableCell>
                      <TableCell align="center">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedItems.map((item) => {
                      const price = getItemPrice(item);
                      return (
                        <TableRow key={item.id}>
                          <TableCell>{item.item_name}</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">₦{parseFloat(price || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">₦{(item.quantity * parseFloat(price || 0)).toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render Orders Tab
 const renderOrdersTab = () => {
 const validOrders = filteredOrders.filter(o => o.status !== 'cancelled');
     const totalRevenue = validOrders.reduce((sum, order) => sum + (parseFloat(order.final_amount) || 0), 0);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayValidOrders = validOrders.filter(o => {
      const orderDate = new Date(o.created_at);
      orderDate.setHours(0, 0, 0, 0);
      return orderDate.getTime() === today.getTime();
    });
    const todayRevenue = todayValidOrders.reduce((sum, order) => sum + (parseFloat(order.final_amount) || 0), 0);
    const todayOrderCount = todayValidOrders.length;
    
    const pendingOrders = filteredOrders.filter(o => o.status === 'pending').length;
    
    const lowStockItems = inventoryItems.filter(item => 
      item.current_stock <= item.min_stock && item.is_active === 1
    ).length;

   const avgOrderValue = validOrders.length > 0 ? totalRevenue / validOrders.length : 0;

    // Check if shift is active
    const isShiftActive = currentShift !== null && !isDayClosed;

    return (
      <Box>
      
        {/* DAY CLOSURE ALERT */}
        {isDayClosed && (
          <Alert severity="error" sx={{ mb: 3 }} icon={<WarningAmber />}>
            <Typography variant="body2">
              <strong>⚠️ DAY IS CLOSED!</strong> No new orders can be created. 
              The cashier has closed the day. Please contact the cashier if you believe this is an error.
            </Typography>
          </Alert>
        )}

        {/* Shift Info Card */}
        {currentShift && !isDayClosed && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#E8F5E9', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Today color="success" />
                <Typography variant="body2">
                  <strong>Active Shift:</strong> Started at {new Date(currentShift.resumed_at).toLocaleTimeString()}
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={showHistory}
                    onChange={(e) => setShowHistory(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <History fontSize="small" />
                    <Typography variant="body2">Show Order History</Typography>
                  </Box>
                }
              />
            </Box>
          </Paper>
        )}

        {/* History Mode Indicator */}
        {showHistory && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<History />}>
            <Typography variant="body2">
              Showing all historical orders from previous shifts. 
              <Button size="small" onClick={() => setShowHistory(false)} sx={{ ml: 1 }}>
                Switch to Current Shift
              </Button>
            </Typography>
          </Alert>
        )}

        {/* Low Stock Alert */}
        {lowStockItems > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
            <Typography variant="body2">
              <strong>Low Stock Alert:</strong> {lowStockItems} item(s) are running low on stock. 
              Please restock soon to avoid running out.
            </Typography>
          </Alert>
        )}

        {/* Search and Action Buttons */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search orders by number, table, room, or guest name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleOpenOrderDialog}
                  disabled={loading || inventoryItems.length === 0 || isDayClosed || !currentShift}
                  sx={{
                    background: (isDayClosed || !currentShift) 
                      ? 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)'
                      : 'linear-gradient(135deg, #0f041f 0%, #330686 100%)',
                    '&:hover': {
                      transform: (isDayClosed || !currentShift) ? 'none' : 'scale(1.05)',
                    },
                    transition: 'transform 0.2s ease',
                  }}
                >
                  New Order
                </Button>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={`Showing ${filteredOrders.length} orders`} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
            {!showHistory && currentShift && (
              <Chip 
                label="Current Shift Only" 
                size="small" 
                color="success" 
                icon={<Today />}
              />
            )}
            {showHistory && (
              <Chip 
                label="Showing History" 
                size="small" 
                color="info" 
                icon={<History />}
                onDelete={() => setShowHistory(false)}
              />
            )}
            {searchQuery && (
              <Chip 
                label={`Search: ${searchQuery}`} 
                size="small" 
                onDelete={() => setSearchQuery('')}
              />
            )}
          </Box>
        </Paper>

        {/* Orders List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {currentOrders.length === 0 ? (
                <Grid item xs={12}>
                  <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <Restaurant sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h5" color="textSecondary" gutterBottom>
                      No Orders Found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      {searchQuery ? 'Try adjusting your search criteria' : 
                       showHistory ? 'No historical orders found' :
                       !currentShift ? 'Please start your shift to see orders' :
                       'Create your first order to get started'}
                    </Typography>
                    {!isDayClosed && currentShift && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenOrderDialog}
                      >
                        Create New Order
                      </Button>
                    )}
                    {!currentShift && !isDayClosed && (
                      <Button
                        variant="contained"
                        startIcon={<PlayArrow />}
                        onClick={handleStartShift}
                        disabled={shiftStarting}
                      >
                        {shiftStarting ? 'Starting...' : 'Start Shift'}
                      </Button>
                    )}
                  </Paper>
                </Grid>
              ) : (
                currentOrders.map((order, index) => (
                  <Grid item xs={12} md={6} lg={4} key={order.id}>
                    <Grow in timeout={index * 100}>
                      <OrderCard status={order.status}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                Order #{order.order_number}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {ORDER_TYPES.find(ot => ot.value === order.order_type)?.label}
                                {order.table_number && ` • Table ${order.table_number}`}
                                {order.room_number && ` • Room ${order.room_number}${order.room_name ? ` (${order.room_name})` : ''}`}
                              </Typography>
                            </Box>
                            <StatusBadge status={order.status}>
                              {ORDER_STATUSES.find(s => s.value === order.status)?.icon}
                              {order.status.toUpperCase()}
                            </StatusBadge>
                          </Box>

                         <List dense>
  {order.items && order.items.slice(0, 3).map((item, idx) => (
    <ListItem key={idx} sx={{ px: 0 }}>
      <ListItemText
        primary={item.item_name}
        secondary={`${item.quantity} x ₦${parseFloat(item.unit_price).toFixed(2)}`}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" fontWeight="bold">
          ₦{(item.quantity * item.unit_price).toFixed(2)}
        </Typography>
             </Box>
    </ListItem>
  ))}
  {order.items && order.items.length > 3 && (
    <ListItem sx={{ px: 0 }}>
      <ListItemText primary={`+${order.items.length - 3} more items`} />
    </ListItem>
  )}
</List>

                          <Divider sx={{ my: 2 }} />
<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
  {/* ADD ITEMS BUTTON */}
  <Tooltip title="Add Items">
    <IconButton
      size="small"
      color="primary"
      onClick={() => handleOpenAddToOrderDialog(order)}
      disabled={['completed'].includes(order.status) || isDayClosed}
    >
      <AddCircle />
    </IconButton>
  </Tooltip>
  
  <Tooltip title="Print Receipt">
    <IconButton size="small" onClick={() => printOrder(order)}>
      <Print />
    </IconButton>
  </Tooltip>
  
  <Tooltip title="View Details">
    <IconButton size="small" onClick={() => {
      setSelectedOrder(order);
      setOpenOrderDetailsDialog(true);
    }}>
      <Visibility />
    </IconButton>
  </Tooltip>
  
 {(order.status === 'confirmed' || order.status === 'pending') && (
  <Button
    size="small"
    variant="contained"
    color="success"
    onClick={() => handleUpdateOrderStatus(order.id, 'completed')}
    startIcon={<CheckCircle />}
    sx={{
      background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
    }}
  >
    Complete Order
  </Button>
)}
</Box>

                        </CardContent>
                      </OrderCard>
                    </Grow>
                  </Grid>
                ))
              )}
            </Grid>

            {filteredOrders.length > ordersRowsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(filteredOrders.length / ordersRowsPerPage)}
                  page={ordersPage + 1}
                  onChange={(event, page) => setOrdersPage(page - 1)}
                  color="primary"
                  size={isMobile ? 'small' : 'large'}
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  // Render Analytics Tab
  const renderAnalyticsTab = () => {
    // Use statistics from API (which now uses fb_payments table)
    const stats = statistics || {};
    
    const totalOrders = stats.total_orders || 0;
    const totalRevenue = stats.total_revenue || 0;
    const averageOrderValue = stats.average_order_value || 0;
    
    const confirmedOrders = stats.confirmed_orders || 0;
const completedOrders = stats.completed_orders || 0;
    
    // Get today's revenue from daily breakdown if available
    let todayRevenue = 0;
    let todayOrderCount = 0;
    if (stats.daily_breakdown && stats.daily_breakdown.length > 0) {
      const today = new Date().toISOString().split('T')[0];
      const todayData = stats.daily_breakdown.find(d => d.date === today);
      if (todayData) {
        todayRevenue = todayData.daily_revenue || 0;
        todayOrderCount = todayData.orders_count || 0;
      }
    }
    
    // Get weekly revenue from API if available
    const weekRevenue = stats.week_revenue || 0;
    const monthRevenue = stats.month_revenue || 0;
    
    // Payment method breakdown from API
    const cashRevenue = stats.cash_revenue || 0;
    const posRevenue = stats.pos_revenue || 0;
    const transferRevenue = stats.transfer_revenue || 0;
    const qrRevenue = stats.qr_revenue || 0;
    const complimentaryCount = stats.complimentary_count || 0;
    const complimentaryAmount = stats.complimentary_amount || 0;

  const statCards = [
  { 
    title: 'Total Orders', 
    value: totalOrders, 
    icon: <Receipt />, 
    color: '#060929', 
    tooltip: 'Total orders (excludes cancelled)' 
  },
  { 
    title: 'Total Revenue', 
    value: `₦${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 
    icon: <AttachMoney />, 
    color: '#4CAF50', 
    tooltip: 'From completed payments only' 
  },
  { 
    title: 'Avg Order Value', 
    value: `₦${averageOrderValue.toFixed(2)}`, 
    icon: <TrendingUp />, 
    color: '#2196F3', 
    tooltip: 'Average value per paid order' 
  },
  // Add confirmed and completed stats
  { 
    title: 'Confirmed Orders', 
    value: confirmedOrders, 
    icon: <CheckCircle />, 
    color: '#1976D2', 
    tooltip: 'Orders awaiting completion' 
  },
  { 
    title: 'Completed Orders', 
    value: completedOrders, 
    icon: <CheckCircle />, 
    color: '#2E7D32', 
    tooltip: 'Completed orders' 
  },
];
    // Item sales - still from orders since payments don't track items
    const itemSales = orders.reduce((acc, order) => {
      if (order.status === 'cancelled') return acc;
      order.items?.forEach(item => {
        const itemName = item.item_name;
        if (!acc[itemName]) {
          acc[itemName] = { quantity: 0, revenue: 0 };
        }
        acc[itemName].quantity += item.quantity;
        acc[itemName].revenue += item.quantity * item.unit_price;
      });
      return acc;
    }, {});

    const popularItems = Object.entries(itemSales)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 5);

    const typeDistribution = ORDER_TYPES.map(type => {
      let matchingOrders;
      if (type.value === 'Lounge') {
        matchingOrders = orders.filter(o => o.order_type === 'Lounge' && o.status !== 'cancelled');
      } else {
        matchingOrders = orders.filter(o => o.order_type === type.value && o.status !== 'cancelled');
      }
      const count = matchingOrders.length;
      const revenue = matchingOrders.reduce((sum, o) => sum + (parseFloat(o.final_amount) || 0), 0);
      return { ...type, count, revenue };
    });

 const statusDistribution = ORDER_STATUSES.map(status => ({
  ...status,
  count: orders.filter(o => o.status === status.value).length
}));

    const lowStockCount = inventoryItems.filter(item => 
      item.current_stock <= item.min_stock && item.is_active === 1
    ).length;

    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{
          background: 'linear-gradient(135deg, #1a024b 0%, #140522 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent',
          mb: 3
        }}>
          📊 F&B Analytics Dashboard
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Note:</strong> Revenue calculations are based on <strong>ACTUAL PAYMENTS RECEIVED</strong> from the fb_payments table. 
            Complimentary orders are excluded from revenue totals.
          </Typography>
        </Alert>

        {/* Payment Method Summary Card */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#E3F2FD', borderRadius: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            💳 Payment Method Breakdown
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">Cash</Typography>
                <Typography variant="h6" sx={{ color: '#4CAF50' }}>₦{cashRevenue.toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">POS Terminal</Typography>
                <Typography variant="h6" sx={{ color: '#2196F3' }}>₦{posRevenue.toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">Bank Transfer</Typography>
                <Typography variant="h6" sx={{ color: '#7B1FA2' }}>₦{transferRevenue.toLocaleString()}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="caption" color="textSecondary">QR Code</Typography>
                <Typography variant="h6" sx={{ color: '#FF9800' }}>₦{qrRevenue.toLocaleString()}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          {statCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Zoom in timeout={index * 100}>
                <StatCard>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: stat.color }}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          {stat.title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px' }}>
                          {stat.tooltip}
                        </Typography>
                      </Box>
                      <Avatar sx={{ bgcolor: stat.color, width: 45, height: 45 }}>
                        {stat.icon}
                      </Avatar>
                    </Box>
                  </CardContent>
                </StatCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* Rest of the component remains the same... */}
        <Grid container spacing={3}>
          {/* Order Outlet Distribution */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Category /> Order Outlet Distribution
                </Typography>
                {totalOrders === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                    No valid orders to display
                  </Typography>
                ) : (
                  <List>
                    {typeDistribution.map((type) => {
                      const percentage = totalOrders > 0 ? (type.count / totalOrders * 100).toFixed(1) : 0;
                      const revenuePercentage = totalRevenue > 0 ? (type.revenue / totalRevenue * 100).toFixed(1) : 0;
                      return (
                        <ListItem key={type.value} divider>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: type.color, width: 40, height: 40 }}>
                              {type.icon}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={type.label}
                            secondary={`${type.count} orders (${percentage}%) • ₦${type.revenue.toLocaleString()} revenue (${revenuePercentage}%)`}
                          />
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" sx={{ color: type.color }}>
                              {type.count}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={parseFloat(percentage)}
                              sx={{ width: 80, borderRadius: 2, mt: 1 }}
                            />
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </StatCard>
          </Grid>

          {/* Order Status Overview */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule /> Order Status Overview
                </Typography>
                <List>
                  {statusDistribution.map((status) => {
                    const percentage = orders.length > 0 ? (status.count / orders.length * 100).toFixed(1) : 0;
                    const isCancelled = status.value === 'cancelled';
                    return (
                      <ListItem key={status.value} divider>
                        <ListItemIcon>
                          <Avatar sx={{ bgcolor: status.color, width: 32, height: 32 }}>
                            {status.icon}
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={status.label}
                          secondary={`${status.count} orders (${percentage}%)`}
                        />
                        <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: status.color }}>
                            {status.count}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={parseFloat(percentage)}
                            sx={{ 
                              width: 80, 
                              borderRadius: 2, 
                              mt: 1,
                              backgroundColor: isCancelled ? '#FFCDD2' : '#E0E0E0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: status.color
                              }
                            }}
                          />
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </StatCard>
          </Grid>

          {/* Most Popular Items */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Fastfood /> Most Popular Items
                </Typography>
                {popularItems.length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                    No sales data available
                  </Typography>
                ) : (
                  <List>
                    {popularItems.map((item, index) => (
                      <ListItem key={index} divider={index < popularItems.length - 1}>
                        <ListItemIcon>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#17064d', minWidth: 35 }}>
                            #{index + 1}
                          </Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          secondary={`${item.quantity} sold • ₦${item.revenue.toLocaleString()} revenue`}
                        />
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {item.quantity} units
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={(item.quantity / popularItems[0].quantity) * 100}
                            sx={{ width: 100, borderRadius: 2, mt: 1 }}
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </StatCard>
          </Grid>

          {/* Inventory Status */}
          <Grid item xs={12} md={6}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Inventory /> Inventory Status
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <Inventory sx={{ color: '#4CAF50' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Total Inventory Items" 
                      secondary={`${inventoryItems.length} active items`}
                    />
                    <Typography variant="h6">{inventoryItems.length}</Typography>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Warning sx={{ color: lowStockCount > 0 ? '#FF9800' : '#4CAF50' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Low Stock Items" 
                      secondary={lowStockCount > 0 ? `${lowStockCount} items need restocking` : "All items well stocked"}
                    />
                    <Typography variant="h6" sx={{ color: lowStockCount > 0 ? '#FF9800' : '#4CAF50' }}>
                      {lowStockCount}
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: '#8BC34A' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="In Stock Items" 
                      secondary={`${inventoryItems.filter(i => i.current_stock > 0).length} items available`}
                    />
                    <Typography variant="h6">
                      {inventoryItems.filter(i => i.current_stock > 0).length}
                    </Typography>
                  </ListItem>
                </List>
              </CardContent>
            </StatCard>
          </Grid>

          {/* Revenue Summary */}
          <Grid item xs={12}>
            <StatCard>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp /> Revenue Summary (Based on Actual Payments)
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="body2" color="textSecondary">Total Revenue</Typography>
                      <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                        ₦{totalRevenue.toLocaleString()}
                      </Typography>
                      <Typography variant="caption">From {totalOrders} paid orders</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="body2" color="textSecondary">Today's Revenue</Typography>
                      <Typography variant="h4" sx={{ color: '#2196F3', fontWeight: 'bold' }}>
                        ₦{todayRevenue.toLocaleString()}
                      </Typography>
                      <Typography variant="caption">{todayOrderCount} paid orders today</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="body2" color="textSecondary">Cash Revenue</Typography>
                      <Typography variant="h4" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
                        ₦{cashRevenue.toLocaleString()}
                      </Typography>
                      <Typography variant="caption">Cash payments only</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="body2" color="textSecondary">Non-Cash Revenue</Typography>
                      <Typography variant="h4" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                        ₦{(posRevenue + transferRevenue + qrRevenue).toLocaleString()}
                      </Typography>
                      <Typography variant="caption">POS + Transfer + QR</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </StatCard>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const tabLabels = [
    { label: 'Orders', icon: <Receipt /> },
    { label: 'Analytics', icon: <TrendingUp /> },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderOrdersTab();
      case 1:
        return renderAnalyticsTab();
      default:
        return renderOrdersTab();
    }
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
          transition: 'margin 0.3s ease',
        }}
      >
        <TopNav
          onLogout={() => navigate('/HotelAdmin')}
          onNotificationClick={() => alert('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        <Paper elevation={0} sx={{
          p: 4,
          mt: 3,
          background: 'linear-gradient(135deg, #0e1da3 0%, #320e65 100%)',
          color: 'white',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
        }}>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🍽️ Food & Beverage Management
            </Typography>
            <Typography variant="subtitle1">
              Manage restaurant, bar, and room service operations
            </Typography>
          </Box>
          <Box sx={{
            position: 'absolute',
            right: -50,
            bottom: -50,
            opacity: 0.1,
            transform: 'rotate(-15deg)',
          }}>
            <Restaurant sx={{ fontSize: 200 }} />
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ mt: 3, borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '16px',
                minWidth: 120,
              },
              '& .Mui-selected': {
                color: '#120b41',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#0a0323',
                height: 3,
              },
            }}
          >
            {tabLabels.map((tab, index) => (
              <Tab
                key={index}
                label={
                  <Badge
                    badgeContent={
                      index === 0 ? filteredOrders.filter(o => o.status !== 'cancelled').length : 0
                    }
                    color="primary"
                  >
                    {tab.label}
                  </Badge>
                }
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      </Box>

      {/* New Order Wizard Dialog */}
      <Dialog
        open={openOrderDialog}
        onClose={handleCloseOrderDialog}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #0c0421 0%, #0a041f 100%)',
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ShoppingCart />
            <Typography variant="h5">Create New Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stepper
            activeStep={activeStep}
            connector={<ColorlibConnector />}
            sx={{ mb: 4, overflowX: 'auto' }}
          >
            {wizardSteps.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={ColorlibStepIconRoot}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && renderStep0()}
          {activeStep === 1 && renderStep1()}
          {activeStep === 2 && renderStep2()}
          {activeStep === 3 && renderStep3()}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseOrderDialog} variant="outlined">
            Cancel
          </Button>
          {activeStep > 0 && (
            <Button onClick={handleBackStep} startIcon={<NavigateBefore />}>
              Back
            </Button>
          )}
          {activeStep < wizardSteps.length - 1 ? (
            <Button
              onClick={handleNextStep}
              variant="contained"
              endIcon={<NavigateNext />}
              sx={{
                background: 'linear-gradient(135deg, #0f041f 0%, #330686 100%)',
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleCreateOrder}
              variant="contained"
              disabled={selectedItems.length === 0}
              sx={{
                background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                '&:hover': { transform: 'scale(1.05)' },
                transition: 'transform 0.2s ease',
              }}
            >
              Place Order (₦{calculateOrderTotal().total.toFixed(2)})
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add to Order Dialog */}
      <Dialog
        open={openAddToOrderDialog}
        onClose={handleCloseAddToOrderDialog}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #0c0421 0%, #0a041f 100%)',
          color: 'white',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AddCircle />
            <Typography variant="h5">
              Add Items to Order #{selectedOrderForAdd?.order_number}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <FormControl fullWidth sx={{ minWidth: 150 }}>
                      <InputLabel>Select Category</InputLabel>
                      <Select
                        value={addToOrderCategory}
                        onChange={handleAddToOrderCategoryChange}
                        label="Select Category"
                      >
                        <MenuItem value="">All Categories</MenuItem>
                        {categories.map(category => (
                          <MenuItem key={category} value={category}>
                            {category}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      placeholder="Search items..."
                      value={addToOrderItemSearch}
                      onChange={(e) => setAddToOrderItemSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      variant={addToOrderIncludeOutOfStock ? "contained" : "outlined"}
                      onClick={() => setAddToOrderIncludeOutOfStock(!addToOrderIncludeOutOfStock)}
                      startIcon={<Inventory />}
                    >
                      {addToOrderIncludeOutOfStock ? "Hide Out of Stock" : "Show Out of Stock"}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Select Items to Add
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto', borderRadius: 2 }}>
                {addToOrderFilteredItems.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Fastfood sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" color="textSecondary">
                      No items found
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Try selecting a different category or add items to inventory
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {addToOrderFilteredItems.map((item) => {
                      const price = getItemPriceForOrder(item, selectedOrderForAdd?.order_type || 'dine_in');
                      const selected = addToOrderItems.some(i => i.id === item.id);
                      const isLowStock = item.current_stock <= item.min_stock;

                      return (
                        <Grid item xs={12} sm={6} md={4} key={item.id}>
                          <InventoryItemCard selected={selected} lowstock={isLowStock} onClick={() => handleAddToOrderItemSelection(item)}>
                            <CardContent>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {item.item_name}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Stock: {item.current_stock} {item.unit}
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#FF6B35' }}>
                                ₦{parseFloat(price || 0).toFixed(2)}
                              </Typography>
                              {selected && (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                                  <IconButton size="small" onClick={(e) => {
                                    e.stopPropagation();
                                    const currentItem = addToOrderItems.find(i => i.id === item.id);
                                    handleAddToOrderQuantityChange(item.id, (currentItem?.quantity || 1) - 1);
                                  }}>
                                    <Remove />
                                  </IconButton>
                                  <Typography variant="body1" fontWeight="bold">
                                    {addToOrderItems.find(i => i.id === item.id)?.quantity || 1}
                                  </Typography>
                                  <IconButton size="small" onClick={(e) => {
                                    e.stopPropagation();
                                    const currentItem = addToOrderItems.find(i => i.id === item.id);
                                    if (currentItem && currentItem.quantity < item.current_stock) {
                                      handleAddToOrderQuantityChange(item.id, (currentItem?.quantity || 0) + 1);
                                    } else {
                                      showSnackbar(`Only ${item.current_stock} ${item.unit} available`, 'warning');
                                    }
                                  }}>
                                    <Add />
                                  </IconButton>
                                </Box>
                              )}
                            </CardContent>
                          </InventoryItemCard>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Paper>
            </Grid>

            {addToOrderItems.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Items to Add
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <List>
                    {addToOrderItems.map((item) => {
                      const price = getItemPriceForOrder(item, selectedOrderForAdd?.order_type || 'dine_in');
                      return (
                        <ListItem key={item.id} divider>
                          <ListItemText
                            primary={item.item_name}
                            secondary={`Quantity: ${item.quantity} × ₦${parseFloat(price || 0).toFixed(2)}`}
                          />
                          <Typography variant="body1" fontWeight="bold" sx={{ mr: 2 }}>
                            ₦{(item.quantity * parseFloat(price || 0)).toFixed(2)}
                          </Typography>
                          <IconButton size="small" onClick={() => handleRemoveAddToOrderItem(item.id)}>
                            <Delete />
                          </IconButton>
                        </ListItem>
                      );
                    })}
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Additional Amount: ₦{calculateAddToOrderTotal().total.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        This will be added to the current order total
                      </Typography>
                    </Box>
                  </List>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseAddToOrderDialog} variant="outlined">Cancel</Button>
          <Button
            onClick={handleAddToOrder}
            variant="contained"
            disabled={addToOrderItems.length === 0}
            sx={{
              background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
            }}
          >
            Add to Order (₦{calculateAddToOrderTotal().total.toFixed(2)})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog open={openCancelDialog} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#09083b', color: 'white' }}>
          Cancel Order #{orderToCancel?.order_number}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
            Are you sure you want to cancel this order? This action cannot be undone.
          </Typography>
          <TextField
            fullWidth
            label="Cancellation Reason *"
            multiline
            rows={3}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="Please provide a reason for cancellation..."
            sx={{ mt: 2 }}
            required
            error={!cancellationReason.trim()}
            helperText={!cancellationReason.trim() ? 'Cancellation reason is required' : ''}
          />
          {orderToCancel && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Order Details:</Typography>
              <Typography variant="body2">Total Amount: ₦{parseFloat(orderToCancel.final_amount || 0).toFixed(2)}</Typography>
              <Typography variant="body2">Items: {orderToCancel.items?.length || 0}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>Keep Order</Button>
          <Button onClick={handleCancelOrder} variant="contained" color="error" disabled={!cancellationReason.trim()}>
            Confirm Cancellation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Item Dialog */}
      <Dialog open={openRemoveItemDialog} onClose={handleCloseRemoveItemDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#dc2626', color: 'white' }}>
          Remove Item from Order
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
            Are you sure you want to remove <strong>{itemToRemove?.item_name}</strong> from order #{selectedOrder?.order_number}?
          </Typography>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>This action will:</strong>
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Remove this item from the order</li>
              <li>Adjust the order total amount</li>
              <li>Restore the item's stock quantity</li>
              <li>Cannot be undone</li>
            </ul>
          </Alert>

          {itemToRemove && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Item Details:</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Item Name:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">{itemToRemove.item_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Quantity:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">{itemToRemove.quantity}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Unit Price:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">₦{parseFloat(itemToRemove.unit_price).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Item Total:</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" fontWeight="bold">
                    ₦{(itemToRemove.quantity * itemToRemove.unit_price).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveItemDialog} disabled={removingItem}>
            Cancel
          </Button>
          <Button
            onClick={handleRemoveItem}
            variant="contained"
            color="error"
            disabled={removingItem}
            startIcon={removingItem ? <CircularProgress size={20} /> : <Delete />}
          >
            {removingItem ? 'Removing...' : 'Remove Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Details Dialog */}
      <Dialog open={openOrderDetailsDialog} onClose={() => setOpenOrderDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Order #{selectedOrder?.order_number} Details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
            <Stepper activeStep={ORDER_STATUSES.findIndex(s => s.value === selectedOrder.status)} sx={{ mb: 3, overflowX: 'auto' }}>
  {ORDER_STATUSES.map((status) => (
    <Step key={status.value}>
      <StepLabel StepIconProps={{ icon: status.icon }}>
        {status.label}
      </StepLabel>
    </Step>
  ))}
</Stepper>

              {selectedOrder.status === 'cancelled' && selectedOrder.cancellation_reason && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Cancellation Reason:</Typography>
                  <Typography variant="body2">{selectedOrder.cancellation_reason}</Typography>
                </Alert>
              )}

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.item_name}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">₦{parseFloat(item.unit_price).toFixed(2)}</TableCell>
                        <TableCell align="right">₦{(item.quantity * item.unit_price).toFixed(2)}</TableCell>
                      <TableCell align="center">
  {/* Remove item button removed for waiters */}
</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Subtotal:</Typography>
                  <Typography>₦{parseFloat(selectedOrder.total_amount || 0).toFixed(2)}</Typography>
                </Box>
                {TAX_RATE > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Tax ({TAX_RATE}%):</Typography>
                    <Typography>₦{parseFloat(selectedOrder.tax_amount || 0).toFixed(2)}</Typography>
                  </Box>
                )}
                {selectedOrder.order_type === 'room_service' && parseFloat(selectedOrder.service_charge || 0) > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Service Charge ({SERVICE_CHARGE_RATE}%):</Typography>
                    <Typography>₦{parseFloat(selectedOrder.service_charge || 0).toFixed(2)}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6" sx={{ color: '#1976D2' }}>
                    ₦{parseFloat(selectedOrder.final_amount || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {selectedOrder.special_instructions && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#FFF3E0', borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Special Instructions:</Typography>
                  <Typography variant="body2">{selectedOrder.special_instructions}</Typography>
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button variant="contained" startIcon={<Print />} onClick={() => printOrder(selectedOrder)} sx={{ bgcolor: '#1976D2' }}>
                  Print Receipt
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOrderDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Pending Orders Dialog - Previous Day Orders */}
      {/* Pending Orders Dialog - Previous Day Orders */}
<Dialog 
  open={pendingOrdersDialogOpen} 
  onClose={() => {
    if (!pendingOrderToComplete) setPendingOrdersDialogOpen(false);
  }} 
  maxWidth="md" 
  fullWidth
>
  <DialogTitle sx={{ background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)', color: 'white' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <WarningAmber />
      <Typography variant="h6">Pending Orders from Previous Days</Typography>
    </Box>
  </DialogTitle>
  <DialogContent>
    <Alert severity="warning" sx={{ mb: 2 }}>
      <Typography variant="body2">
        <strong>⚠️ Cannot start new shift!</strong> You have {pendingOrdersCheck.pending_count} pending order(s) from previous days.
        These must be <strong>COMPLETED</strong> (not cancelled) before starting a new shift.
        <br /><br />
        <em>Note: Completion will be backdated to the original order date.</em>
      </Typography>
    </Alert>
    
    {pendingOrdersCheck.pending_orders?.length > 0 && (
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Order #</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Guest</strong></TableCell>
              <TableCell><strong>Table/Room</strong></TableCell>
              <TableCell><strong>Items</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Created Date</strong></TableCell>
              <TableCell align="center"><strong>Action</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingOrdersCheck.pending_orders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>{order.order_number}</TableCell>
                <TableCell>
                  <Chip 
                    size="small" 
                    label={order.order_type === 'dine_in' ? 'Bar' : order.order_type === 'Lounge' ? 'Lounge' : order.order_type}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{order.guest_name}</TableCell>
                <TableCell>{order.table_number || order.room_number || '-'}</TableCell>
                <TableCell>
                  {order.items?.slice(0, 2).map((item, i) => (
                    <Typography key={i} variant="caption" display="block">
                      {item.quantity}x {item.item_name}
                    </Typography>
                  ))}
                  {order.items?.length > 2 && (
                    <Typography variant="caption" color="textSecondary">
                      +{order.items.length - 2} more
                    </Typography>
                  )}
                </TableCell>
                <TableCell>₦{parseFloat(order.final_amount || 0).toFixed(2)}</TableCell>
                <TableCell>
                  <Chip 
                    size="small" 
                    label={new Date(order.created_at).toLocaleDateString()} 
                    color="warning"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Button 
                    size="small" 
                    color="success" 
                    variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={() => {
                      setPendingOrderToComplete(order);
                    }}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Complete Order
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
    
    {/* Complete Order Confirmation */}
    {pendingOrderToComplete && (
      <Paper sx={{ mt: 3, p: 3, bgcolor: '#E8F5E9', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CheckCircle sx={{ color: '#4CAF50' }} />
          <Typography variant="h6" sx={{ color: '#2E7D32' }}>
            Complete Order: {pendingOrderToComplete.order_number}
          </Typography>
        </Box>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>⚠️ Important:</strong> This order will be marked as COMPLETED and backdated to the original creation date: 
            <strong> {new Date(pendingOrderToComplete.created_at).toLocaleString()}</strong>
            <br /><br />
            This ensures the waiter gets credited for their work on the correct day.
          </Typography>
        </Alert>
        
        <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Order Summary:</Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Order Date:</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">{new Date(pendingOrderToComplete.created_at).toLocaleString()}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Items:</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">{pendingOrderToComplete.items?.length || 0} item(s)</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Total Amount:</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" fontWeight="bold">₦{parseFloat(pendingOrderToComplete.final_amount || 0).toFixed(2)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">Waiter:</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">{pendingOrderToComplete.created_by_name || 'Unknown'}</Typography>
            </Grid>
          </Grid>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button 
            variant="outlined" 
            onClick={() => {
              setPendingOrderToComplete(null);
            }}
          >
            Back
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleCompletePendingOrder}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
            sx={{ 
              background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
              '&:hover': { transform: 'scale(1.02)' }
            }}
          >
            {loading ? 'Completing...' : `Complete Order (Backdate to ${new Date(pendingOrderToComplete.created_at).toLocaleDateString()})`}
          </Button>
        </Box>
      </Paper>
    )}
  </DialogContent>
  <DialogActions>
    <Button 
      onClick={() => {
        setPendingOrdersDialogOpen(false);
        setPendingOrderToComplete(null);
      }}
      disabled={!!pendingOrderToComplete}
    >
      Close
    </Button>
  </DialogActions>
</Dialog>

      <FloatingActionButton 
        color="primary" 
        onClick={handleOpenOrderDialog}
        disabled={isDayClosed || !currentShift}
      >
        <Add />
      </FloatingActionButton>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelFBMgmt;